import { useState, type CSSProperties } from 'react'
import { formatTRY } from '../../utils/money'
import { useCatalog } from '../../context/CatalogContext'
import { hallColor } from '../../utils/hallTones'
import { tableLabel, displayTable } from '../../utils/tableLabel'
import type { Order, Table } from '../../types'
import { NoteIcon } from '../icons'

interface Props {
  tables: Table[]
  totalKurus: number
  /** Sepetteki ürün adedi, tutar kutusunda "3 ürün". */
  itemCount?: number
  /** Tickets still unpaid, so a table already in use can't be opened twice. */
  openTickets: Order[]
  onPick: (table: Table) => void
  /** Adds the cart to the ticket already open on that table. */
  onAddToTicket: (ticket: Order) => void
  onCancel: () => void
  /**
   * Garson önce masa seçiyor (25 Eki 2026): sepet henüz boş, tutar kutusu
   * yok, dolu masaya dokunmak onay sormadan o masayı seçiyor (adisyonu
   * açılıyor, eklenenler ona gidiyor).
   */
  pickOnly?: boolean
}

/** Picks the table a waiter's ticket belongs to. */
export function TableDialog({
  tables,
  totalKurus,
  itemCount,
  openTickets,
  onPick,
  onAddToTicket,
  onCancel,
  pickOnly = false,
}: Props) {
  const { halls } = useCatalog()
  /**
   * Two waiters serving the same table used to open two tickets on it, and the
   * customer paid one of them. A table with an unpaid ticket can only be added
   * to, so everything ordered there stays on one bill.
   */
  const ticketByTable = new Map(
    openTickets.filter((t) => t.tableName).map((t) => [t.tableName as string, t]),
  )

  /** Set once a busy table is tapped, cleared on confirm or cancel. */
  const [confirming, setConfirming] = useState<Order | null>(null)

  // Adding to someone else's open ticket changes a bill that is already
  // running, and the tables sit close enough together to mis-tap. A fresh
  // table needs no confirmation, nothing exists to disturb yet.
  if (confirming) {
    return (
      <div className="dlg" role="dialog" aria-modal="true">
        <div className="dlg__card">
          <h2>{displayTable(confirming.tableName)} adisyonuna ekle</h2>

          <p className="dlg__empty">
            Bu masada <strong>{formatTRY(confirming.totalKurus)}</strong> tutarında açık
            adisyon var
            {confirming.openedByName && <> ({confirming.openedByName})</>}. Sepetteki{' '}
            <strong>{formatTRY(totalKurus)}</strong> o adisyona eklenecek, yeni toplam{' '}
            <strong>{formatTRY(confirming.totalKurus + totalKurus)}</strong> olacak.
          </p>

          <div className="dlg__buttons">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setConfirming(null)}
            >
              Vazgeç
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => onAddToTicket(confirming)}
            >
              Ekle
            </button>
          </div>
        </div>
      </div>
    )
  }

  /**
   * Salon salon: "Bahçe 3" ile "Salon 3" farklı masalar ve garson onları
   * bulunduğu bölümle ayırıyor. Salonlar Ayarlar'daki sırayla, salonsuz
   * masalar en sonda. Hiç salon yoksa başlıksız tek liste, bugünkü gibi.
   */
  const groups = (() => {
    const known = new Set(halls.map((h) => h.id))
    const out = halls
      .map((h) => ({ key: h.id, title: h.name, tables: tables.filter((t) => t.hallId === h.id) }))
      .filter((g) => g.tables.length > 0)
    const loose = tables.filter((t) => !t.hallId || !known.has(t.hallId))
    if (loose.length > 0) out.push({ key: 'salonsuz', title: out.length > 0 ? 'Salonsuz' : '', tables: loose })
    return out
  })()

  return (
    <div className="dlg" role="dialog" aria-modal="true">
      <div className="dlg__card dlg__card--wide">
        <h2>{pickOnly ? 'Hangi masa?' : 'Masa Seç'}</h2>

        {/* Tutar kutusu (22 Eki 2026): neyin kaydedileceği tek bakışta,
            solda kaç ürün, sağda tutar. */}
        {pickOnly ? (
          <p className="dlg__empty tablepick__lead">
            Masayı seçin, sonra ürünleri ekleyin. Açık masayı seçerseniz eklenenler o adisyona gider.
          </p>
        ) : (
        <div className="tablepick__sum">
          <span className="tablepick__sum-text">
            <span>Adisyona eklenecek</span>
            {itemCount !== undefined && <small>{itemCount} ürün</small>}
          </span>
          <strong>{formatTRY(totalKurus)}</strong>
        </div>
        )}

        {tables.length === 0 ? (
          <p className="dlg__empty">
            Masa tanımlanmamış. Ayarlar sayfasından masa ekleyin.
          </p>
        ) : (
          <div className="tablepick__groups">
            {groups.map((g) => (
              <section
                key={g.key}
                className="tablepick__group"
                // Salonun rengi (22 Eki 2026), Masa Yönetimi'ndekiyle aynı.
                // Salon yoksa renk de yok: tek oda, ayırt edilecek bir şey yok.
                style={
                  halls.length > 0
                    ? ({ '--hall': hallColor(g.key === 'salonsuz' ? null : g.key, halls) } as CSSProperties)
                    : undefined
                }
              >
                {g.title && <h3 className="tablepick__hall">{g.title}</h3>}
                <div className="tablepick">
                  {g.tables.map((t) => {
                    const busy = ticketByTable.get(tableLabel(t, halls))
                    return (
                      <button
                        key={t.id}
                        type="button"
                        className={busy ? 'is-busy' : ''}
                        onClick={() => (busy && !pickOnly ? setConfirming(busy) : onPick(t))}
                        title={
                          busy
                            ? `${displayTable(tableLabel(t, halls))} açık. Bu sipariş mevcut adisyona eklenir` +
                              (busy.openedByName ? ` (${busy.openedByName})` : '')
                            : undefined
                        }
                      >
                        {t.name}
                        {busy?.tableNote && (
                          <span className="tablepick__note" title={busy.tableNote} aria-label="Masa notu var">
                            <NoteIcon />
                          </span>
                        )}
                        {busy && (
                          <span className="tablepick__busy">{formatTRY(busy.totalKurus)}</span>
                        )}
                      </button>
                    )
                  })}
                </div>
              </section>
            ))}
          </div>
        )}

        <div className="dlg__buttons">
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Vazgeç
          </button>
        </div>
      </div>
    </div>
  )
}
